import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export type Granularity = "day" | "week" | "month" | "year";
export type ReportCurrency = "USD" | "TRY" | "EUR";
export type ProductGroupBy = "category" | "sap";

export interface SalesOverview {
	currency: string;
	range: { from: string; to: string; granularity: Granularity };
	sales: {
		amount: number;
		orderCount: number;
		averageOrderValue: number;
		missingRateCount: number;
	};
	offers: {
		count: number;
		won: number;
		lost: number;
		pending: number;
		wonAmount: number;
		lostAmount: number;
		winRate: number | null;
	};
	target: { amount: number; achievementPercent: number | null };
	salesByCurrency: Array<{
		currency: string;
		amount: number;
		orderCount: number;
		missingRateCount: number;
	}>;
	series: Array<{ bucket: string; amount: number; orderCount: number; offerCount: number }>;
}

export interface SalesBreakdowns {
	currency: string;
	salespeople: Array<{ userId: string | null; name: string; amount: number; orderCount: number }>;
	customers: Array<{ companyId: string | null; name: string; amount: number; orderCount: number }>;
	regions: Array<{ region: string; amount: number; orderCount: number }>;
	productGroups: Array<{ productGroup: string; amount: number; quantity: number }>;
	productGroupBy: ProductGroupBy;
}

export interface LeaderboardEntry {
	userId: string | null;
	name: string;
	count: number;
	amount?: number;
}

export interface Leaderboard {
	currency: string;
	period: "week" | "month";
	topOfferCreators: LeaderboardEntry[];
	topOrderCreators: LeaderboardEntry[];
	topWon: LeaderboardEntry[];
	topLost: LeaderboardEntry[];
}

export interface SalesTarget {
	id: string;
	year: number;
	/** 0 = yıllık hedef, 1-12 = ilgili ay */
	month: number;
	userId: string | null;
	amount: string;
	currency: string;
	userName?: string | null;
	userEmail?: string | null;
}

interface RangeParams {
	from: string;
	to: string;
}

const qs = (params: Record<string, string | number | undefined>) =>
	Object.entries(params)
		.filter(([, v]) => v !== undefined && v !== "")
		.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
		.join("&");

export function useSalesOverview(
	params: RangeParams & { granularity: Granularity; currency: ReportCurrency },
) {
	return useQuery({
		queryKey: ["reports", "overview", params],
		queryFn: () => api.get<SalesOverview>(`/api/reports/sales/overview?${qs({ ...params })}`),
	});
}

export function useSalesBreakdowns(
	params: RangeParams & {
		productGroupBy: ProductGroupBy;
		currency: ReportCurrency;
		limit?: number;
	},
) {
	return useQuery({
		queryKey: ["reports", "breakdowns", params],
		queryFn: () => api.get<SalesBreakdowns>(`/api/reports/sales/breakdowns?${qs({ ...params })}`),
	});
}

export function useLeaderboard(period: "week" | "month", currency: ReportCurrency) {
	return useQuery({
		queryKey: ["reports", "leaderboard", period, currency],
		queryFn: () => api.get<Leaderboard>(`/api/reports/leaderboard?${qs({ period, currency })}`),
	});
}

export function useSalesTargets(year: number) {
	return useQuery({
		queryKey: ["sales-targets", year],
		queryFn: () => api.get<SalesTarget[]>(`/api/admin/sales-targets?year=${year}`),
	});
}

export function useSaveSalesTarget() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			year: number;
			month: number;
			userId?: string | null;
			amount: number;
			currency?: string;
		}) => api.put<SalesTarget>("/api/admin/sales-targets", input),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["sales-targets"] });
			void queryClient.invalidateQueries({ queryKey: ["reports"] });
		},
	});
}

export function useDeleteSalesTarget() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/admin/sales-targets/${id}`),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["sales-targets"] });
			void queryClient.invalidateQueries({ queryKey: ["reports"] });
		},
	});
}
